import React from 'react';
import { GridColumnMenuFilter, GridColumnMenuProps } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { TextArea } from '@progress/kendo-react-inputs';
import { createFieldFilter, extractFieldFilter, extractFilterValues, removeFieldFilter } from './GridFilterHelper';
import { SvgIcon } from '@progress/kendo-react-common';
import { filterIcon } from '@progress/kendo-svg-icons';
import { useGlobalizationContext, useTranslation } from '@cs-cms/state-management';
import { MAX_BULK_PASTE_COUNT } from '@cs-cms/types';
import { gridFilterOperators } from '../helpers/gridFilterOperators';

interface BulkColumnMenuFilterProps extends GridColumnMenuProps {
  field: string;
  expanded: boolean;
  onExpandChange: (expanded: boolean) => void;
}

interface BulkFilterState {
  selectedValues: string[];
  bulkInput: string;
  activeTab: 'individual' | 'bulk';
  bulkInputError: string | null;
}

export const BulkColumnMenuFilter: React.FC<BulkColumnMenuFilterProps> = props => {
  const { field, expanded, onExpandChange, filter, onFilterChange } = props;
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const [filterState, setFilterState] = React.useState<BulkFilterState>(() => {
    const initialValues = filter
      ? (() => {
          const fieldFilter = extractFieldFilter(filter, field);
          return fieldFilter ? extractFilterValues(fieldFilter) : [];
        })()
      : [];

    const hasMultipleValues = initialValues.length > 1;
    const initialBulkInput = hasMultipleValues ? initialValues.join('\n') : '';

    return {
      selectedValues: initialValues,
      bulkInput: initialBulkInput,
      activeTab: hasMultipleValues ? 'bulk' : 'individual',
      bulkInputError: null
    };
  });

  const validateBulkInput = (input: string): string | null => {
    if (!input.trim()) {
      return null;
    }

    const inputValues = input
      .split(/[\n\t,; ]+/)
      .map(item => item.trim())
      .filter(item => item.length > 0);

    if (inputValues.length > MAX_BULK_PASTE_COUNT) {
      return translate('grid.bulkFilter.tooManyItemsError', { max: MAX_BULK_PASTE_COUNT, count: inputValues.length });
    }

    return null;
  };

  const handleFilterApply = (event?: React.SyntheticEvent) => {
    const syntheticEvent = event || (new CustomEvent('filter') as unknown as React.SyntheticEvent<any>);

    let finalSelectedValues = filterState.selectedValues;
    if (filterState.activeTab === 'bulk' && filterState.bulkInput.trim()) {
      const error = validateBulkInput(filterState.bulkInput);
      if (error) {
        setFilterState(prev => ({ ...prev, bulkInputError: error }));
        return;
      }

      const inputValues = filterState.bulkInput
        .split(/[\n\t,; ]+/)
        .map(item => item.trim())
        .filter(item => item.length > 0);
      finalSelectedValues = [...new Set([...filterState.selectedValues, ...inputValues])];
    }

    if (finalSelectedValues.length === 0) {
      const newFilter = removeFieldFilter(filter, field);
      onFilterChange?.(newFilter, syntheticEvent);
    } else {
      const newFilter = createFieldFilter(filter, field, finalSelectedValues);
      onFilterChange?.(newFilter, syntheticEvent);
    }
    onExpandChange(false);
  };

  const handleClearAll = () => {
    setFilterState(prev => ({
      ...prev,
      selectedValues: [],
      bulkInput: ''
    }));
  };

  if (!expanded) {
    return (
      <div className="k-columnmenu-item" onClick={() => onExpandChange(true)} style={{ cursor: 'pointer' }}>
        <div>
          <SvgIcon icon={filterIcon} />
          <span>{translate('grid.filterTitle')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="k-columnmenu-item k-columnmenu-item-wrapper">
      <div className="k-columnmenu-item-content">
        <div className="bulkFilterContainer">
          <div className="bulkFilterTabs">
            <Button
              className={`tabButton ${filterState.activeTab === 'individual' ? 'active' : ''}`}
              onClick={() => setFilterState(prev => ({ ...prev, activeTab: 'individual' }))}
              themeColor={'primary'}
              fillMode="flat"
            >
              {translate('grid.bulkFilter.tabIndividual')}
            </Button>
            <Button
              className={`tabButton ${filterState.activeTab === 'bulk' ? 'active' : ''}`}
              onClick={() => setFilterState(prev => ({ ...prev, activeTab: 'bulk' }))}
              fillMode="flat"
            >
              {translate('grid.bulkFilter.tabBulkPaste')}
            </Button>
          </div>

          {filterState.activeTab === 'individual' && (
            <div className="individualFilterContent" onMouseDown={e => e.stopPropagation()}>
              <GridColumnMenuFilter
                column={props.column}
                filter={props.filter}
                onFilterChange={props.onFilterChange}
                onCloseMenu={props.onCloseMenu}
                hideSecondFilter={true}
                filterOperators={gridFilterOperators}
                expanded={true}
                filterable={true}
                alwaysExpand
              />
            </div>
          )}

          {filterState.activeTab === 'bulk' && (
            <div className="bulkFilterContent">
              <TextArea
                placeholder={translate('grid.bulkFilter.bulkTextareaPlaceholder')}
                value={filterState.bulkInput}
                onChange={e =>
                  setFilterState(prev => ({
                    ...prev,
                    bulkInput: e.value || ''
                  }))
                }
                rows={6}
                resizable="vertical"
                onMouseDown={e => e.stopPropagation()}
              />

              <p className="bulkInputHelp">{translate('grid.bulkFilter.bulkInputHelp')}</p>

              {filterState.bulkInputError && <div className="bulkInputError">{filterState.bulkInputError}</div>}

              {filterState.selectedValues.length > 0 && (
                <div className="bulkSelections">
                  {filterState.selectedValues.length} {translate('grid.bulkFilter.selectedItemsCounter')}
                </div>
              )}

              <div className="bulkFilterActions">
                <Button className="filterButton" onClick={handleFilterApply} themeColor="primary">
                  {translate('grid.bulkFilter.filterButton')}
                </Button>
                <Button className="clearButton" onClick={handleClearAll} themeColor="base">
                  {translate('grid.bulkFilter.clearButton')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
